import { useState } from 'react';
import {
  Settings,
  Check,
  GripVertical,
  Filter as FilterIcon,
  Palette,
  ArrowUpAZ,
  ArrowDownZA,
  Group,
  Plus,
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { ColumnManagerDialog } from './ColumnManagerDialog';
import {
  CalendarConfig,
  ChartAggregation,
  ChartConfig,
  ChartType,
  ColumnDef,
  ColumnId,
  CustomColumnDef,
  DateFilterState,
  DisplayOptions as DisplayOptionsState,
  FilterState,
  FormatsState,
  RowSpacing,
  SortState,
  ViewMode,
} from './types';

interface DisplayOptionsProps {
  value: DisplayOptionsState;
  columns: ColumnDef<any>[];
  viewMode: ViewMode;
  calendarConfig: CalendarConfig | undefined;
  chartConfig: ChartConfig | undefined;
  columnOrder: ColumnId[] | undefined;
  filters: FilterState;
  dateFilters: DateFilterState;
  sort: SortState;
  formats: FormatsState;
  groupBy: ColumnId | undefined;
  customColumns: CustomColumnDef[];
  onChange: (next: DisplayOptionsState) => void;
  onCalendarChange: (next: CalendarConfig | undefined) => void;
  onChartChange: (next: ChartConfig | undefined) => void;
  onReorder: (next: ColumnId[] | undefined) => void;
  onAddCustomColumn: (def: CustomColumnDef) => void;
  onRemoveCustomColumn: (id: ColumnId) => void;
}

export function DisplayOptions({
  value,
  columns,
  viewMode,
  calendarConfig,
  chartConfig,
  columnOrder,
  filters,
  dateFilters,
  sort,
  formats,
  groupBy,
  customColumns,
  onChange,
  onCalendarChange,
  onChartChange,
  onReorder,
  onAddCustomColumn,
  onRemoveCustomColumn,
}: DisplayOptionsProps) {
  const setRowSpacing = (rowSpacing: RowSpacing) => onChange({ ...value, rowSpacing });
  const toggleZebra = () => onChange({ ...value, zebra: !value.zebra });
  const toggleColumn = (id: ColumnId) => {
    const next = new Set(value.hiddenColumns);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ...value, hiddenColumns: next });
  };

  const [columnManagerOpen, setColumnManagerOpen] = useState(false);

  // A column is "active" if it's visible OR referenced by any view-state
  // (filter / date-filter / sort / format / group-by). Hidden+unreferenced
  // columns are noise — the modal handles the full registry list.
  const isColumnActive = (col: ColumnDef<any>): boolean => {
    if (!value.hiddenColumns.has(col.id)) return true;
    if ((filters[col.id]?.size ?? 0) > 0) return true;
    if (dateFilters[col.id]) return true;
    if (sort.some((s) => s.columnId === col.id)) return true;
    const fmt = formats[col.id];
    if (fmt && (Array.isArray(fmt) ? fmt.length > 0 : true)) return true;
    if (groupBy === col.id) return true;
    return false;
  };

  // All active columns in one flat list (no basic/related/config separation),
  // preserving the table's actual column order so drag-to-reorder lines up.
  const activeCols = columns.filter(isColumnActive);
  const dateCols = columns.filter((c) => c.dataType === 'date');
  const numericCols = columns.filter((c) => c.dataType === 'number');

  /**
   * Move `draggedId` to immediately before `targetId` in the columnOrder
   * (creating an order array seeded from the current column array if none
   * exists yet). When `targetId` is null, the column moves to the end.
   */
  const moveColumn = (draggedId: ColumnId, targetId: ColumnId | null) => {
    const seed = columnOrder && columnOrder.length > 0
      ? columnOrder.slice()
      : columns.map((c) => c.id);
    const fromIdx = seed.indexOf(draggedId);
    if (fromIdx === -1) return;
    seed.splice(fromIdx, 1);
    if (!targetId) {
      seed.push(draggedId);
    } else {
      const toIdx = seed.indexOf(targetId);
      seed.splice(toIdx === -1 ? seed.length : toIdx, 0, draggedId);
    }
    onReorder(seed);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex items-center justify-center w-8 h-8 rounded-sm text-stone-700 hover:bg-stone-100 transition-colors"
          title="Display options"
        >
          <Settings className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 max-h-[80vh] overflow-y-auto rounded-sm">
        <div className="px-3 py-2.5 border-b border-stone-200 sticky top-0 bg-white z-10">
          <div className="text-sm font-semibold text-stone-900">Display options</div>
        </div>

        <div className="py-1">
        {viewMode === 'calendar' && (
          <div className="px-3 py-1">
            <SectionLabel>Calendar</SectionLabel>
            <FieldRow label="Date column">
              <select
                value={calendarConfig?.dateColumnId ?? dateCols[0]?.id ?? ''}
                onChange={(e) => {
                  if (!e.target.value) {
                    onCalendarChange(undefined);
                    return;
                  }
                  onCalendarChange({
                    dateColumnId: e.target.value,
                    showWeekends: calendarConfig?.showWeekends,
                  });
                }}
                className="w-full h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors"
              >
                {dateCols.length === 0 && <option value="">No date columns</option>}
                {dateCols.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </FieldRow>
            <label className="flex items-center gap-2 px-1 py-1.5 cursor-pointer">
              <Checkbox
                checked={calendarConfig?.showWeekends !== false}
                onCheckedChange={(v) =>
                  onCalendarChange({
                    dateColumnId: calendarConfig?.dateColumnId ?? dateCols[0]?.id ?? '',
                    showWeekends: !!v,
                  })
                }
              />
              <span className="text-xs text-stone-700">Show weekends</span>
            </label>
            <p className="px-1 pt-1 pb-0 text-[11px] text-stone-400">
              Each day's card shows whatever columns are visible below.
            </p>
          </div>
        )}
        {viewMode === 'calendar' && <MenuDivider />}

        {viewMode === 'chart' && (
          <>
            <ChartSection
              chartConfig={chartConfig}
              columns={columns}
              numericCols={numericCols}
              onChange={onChartChange}
            />
            <MenuDivider />
          </>
        )}

        <OptionRow
          label="Compact spacing"
          selected={value.rowSpacing === 'compact'}
          onClick={() => setRowSpacing('compact')}
        />
        <OptionRow
          label="Tight spacing"
          selected={value.rowSpacing === 'tight'}
          onClick={() => setRowSpacing('tight')}
        />
        <OptionRow
          label="Loose spacing"
          selected={value.rowSpacing === 'loose'}
          onClick={() => setRowSpacing('loose')}
        />

        <MenuDivider />

        <OptionRow
          label="Highlight alternating rows"
          selected={value.zebra}
          onClick={toggleZebra}
        />

        <MenuDivider />

        <button
          onClick={() => setColumnManagerOpen(true)}
          className="w-full flex items-center gap-2 px-3 h-8 rounded-sm text-[13px] font-medium text-[#0078d4] hover:bg-[#0078d4]/10 text-left transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add/remove columns
        </button>
        <ColumnList
          cols={activeCols}
          hiddenColumns={value.hiddenColumns}
          filters={filters}
          dateFilters={dateFilters}
          sort={sort}
          formats={formats}
          groupBy={groupBy}
          onToggle={toggleColumn}
          onMove={moveColumn}
        />
        </div>
      </PopoverContent>
      <ColumnManagerDialog
        open={columnManagerOpen}
        onOpenChange={setColumnManagerOpen}
        columns={columns}
        hiddenColumns={value.hiddenColumns}
        filters={filters}
        dateFilters={dateFilters}
        sort={sort}
        formats={formats}
        groupBy={groupBy}
        customColumns={customColumns}
        onToggle={toggleColumn}
        onMove={moveColumn}
        onAddCustomColumn={onAddCustomColumn}
        onRemoveCustomColumn={onRemoveCustomColumn}
      />
    </Popover>
  );
}

interface ChartSectionProps {
  chartConfig: ChartConfig | undefined;
  columns: ColumnDef<any>[];
  numericCols: ColumnDef<any>[];
  onChange: (next: ChartConfig | undefined) => void;
}

function ChartSection({ chartConfig, columns, numericCols, onChange }: ChartSectionProps) {
  const type: ChartType = chartConfig?.type ?? 'bar';
  const aggregation: ChartAggregation = chartConfig?.aggregation ?? 'count';
  const xAxisColumn = chartConfig?.xAxisColumn ?? '';
  const valueColumn = chartConfig?.valueColumn ?? '';
  const useConditionalColors = !!chartConfig?.useConditionalColors;

  const patch = (p: Partial<ChartConfig>) => {
    const merged: ChartConfig = {
      type,
      xAxisColumn,
      aggregation,
      valueColumn: valueColumn || undefined,
      useConditionalColors,
      ...p,
    };
    if (!merged.xAxisColumn) {
      onChange(undefined);
      return;
    }
    // Count aggregation doesn't use valueColumn
    if (merged.aggregation === 'count') merged.valueColumn = undefined;
    onChange(merged);
  };

  return (
    <div className="px-3 py-1">
      <SectionLabel>Chart</SectionLabel>
      <FieldRow label="Chart type">
        <div className="grid grid-cols-3 gap-1 w-full">
          {(['bar', 'pie', 'line'] as ChartType[]).map((t) => (
            <button
              key={t}
              onClick={() => patch({ type: t })}
              className={cn(
                'px-2 py-1.5 rounded-sm text-xs font-medium transition-colors',
                type === t
                  ? 'bg-[#0078d4] text-white hover:bg-[#106ebe]'
                  : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              )}
            >
              {capitalize(t)}
            </button>
          ))}
        </div>
      </FieldRow>
      <FieldRow label="X-axis">
        <select
          value={xAxisColumn}
          onChange={(e) => patch({ xAxisColumn: e.target.value })}
          className="w-full h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors"
        >
          <option value="">Pick a column</option>
          {columns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </FieldRow>
      <FieldRow label="Y-axis">
        <div className="space-y-1 w-full">
          <select
            value={aggregation}
            onChange={(e) => patch({ aggregation: e.target.value as ChartAggregation })}
            className="w-full h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors"
          >
            <option value="count">Count of rows</option>
            <option value="sum">Sum</option>
            <option value="avg">Average</option>
            <option value="min">Minimum</option>
            <option value="max">Maximum</option>
          </select>
          {aggregation !== 'count' && (
            <select
              value={valueColumn}
              onChange={(e) => patch({ valueColumn: e.target.value || undefined })}
              className="w-full h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors"
            >
              <option value="">Pick a numeric column</option>
              {numericCols.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </FieldRow>
      <label className="flex items-center gap-2 px-1 py-1.5 cursor-pointer">
        <Checkbox
          checked={useConditionalColors}
          onCheckedChange={(v) => patch({ useConditionalColors: !!v })}
        />
        <span className="text-xs text-stone-700">Inherit conditional-format colors</span>
      </label>
    </div>
  );
}

interface OptionRowProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

function OptionRow({ label, selected, onClick }: OptionRowProps) {
  return (
    <button
      className={cn(
        'w-full flex items-center justify-between px-3 h-8 rounded-sm text-[13px] text-left transition-colors',
        selected
          ? 'bg-[#0078d4]/10 text-[#0078d4] font-medium'
          : 'hover:bg-stone-100 text-stone-900'
      )}
      onClick={onClick}
    >
      <span>{label}</span>
      {selected && <Check className="w-4 h-4 text-[#0078d4]" />}
    </button>
  );
}

export interface ColumnListProps {
  cols: ColumnDef<any>[];
  hiddenColumns: Set<ColumnId>;
  filters: FilterState;
  dateFilters: DateFilterState;
  sort: SortState;
  formats: FormatsState;
  groupBy: ColumnId | undefined;
  onToggle: (id: ColumnId) => void;
  onMove: (draggedId: ColumnId, targetId: ColumnId | null) => void;
}

export function ColumnList({
  cols,
  hiddenColumns,
  filters,
  dateFilters,
  sort,
  formats,
  groupBy,
  onToggle,
  onMove,
}: ColumnListProps) {
  const [draggingId, setDraggingId] = useState<ColumnId | null>(null);
  const [dropTargetId, setDropTargetId] = useState<ColumnId | null>(null);

  return (
    <div
      onDragOver={(e) => {
        if (draggingId) e.preventDefault();
      }}
      onDrop={(e) => {
        if (draggingId) {
          e.preventDefault();
          onMove(draggingId, dropTargetId);
        }
        setDraggingId(null);
        setDropTargetId(null);
      }}
    >
      {cols.map((col) => {
        const hasFilter =
          (filters[col.id]?.size ?? 0) > 0 || !!dateFilters[col.id];
        const formatRules = formats[col.id];
        const hasFormat = Array.isArray(formatRules)
          ? formatRules.length > 0
          : !!formatRules;
        const sortEntry = sort.find((s) => s.columnId === col.id);
        const isGroupedBy = groupBy === col.id;
        return (
          <div
            key={col.id}
            draggable
            onDragStart={(e) => {
              setDraggingId(col.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => {
              setDraggingId(null);
              setDropTargetId(null);
            }}
            onDragOver={(e) => {
              if (!draggingId || draggingId === col.id) return;
              e.preventDefault();
              e.stopPropagation();
              setDropTargetId(col.id);
            }}
            onDrop={(e) => {
              if (!draggingId) return;
              e.preventDefault();
              e.stopPropagation();
              onMove(draggingId, col.id);
              setDraggingId(null);
              setDropTargetId(null);
            }}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-sm hover:bg-stone-100 group transition-colors',
              draggingId === col.id && 'opacity-50',
              dropTargetId === col.id && draggingId && draggingId !== col.id && 'bg-[#0078d4]/10'
            )}
          >
            <GripVertical className="w-3.5 h-3.5 text-stone-300 group-hover:text-stone-500 cursor-grab active:cursor-grabbing shrink-0" />
            <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
              <Checkbox
                checked={!hiddenColumns.has(col.id)}
                onCheckedChange={() => onToggle(col.id)}
              />
              <span className="text-sm text-stone-900 truncate">{col.label}</span>
            </label>
            <div className="flex items-center gap-1 shrink-0 text-stone-400">
              {hasFilter && (
                <FilterIcon
                  className="w-3 h-3 text-[#0078d4] fill-[#0078d4]"
                  aria-label="Filter active"
                />
              )}
              {sortEntry && (
                sortEntry.direction === 'asc' ? (
                  <ArrowUpAZ
                    className="w-3 h-3 text-[#0078d4]"
                    aria-label="Sorted ascending"
                  />
                ) : (
                  <ArrowDownZA
                    className="w-3 h-3 text-[#0078d4]"
                    aria-label="Sorted descending"
                  />
                )
              )}
              {hasFormat && (
                <Palette
                  className="w-3 h-3 text-stone-500"
                  aria-label="Conditional format active"
                />
              )}
              {isGroupedBy && (
                <Group
                  className="w-3 h-3 text-amber-500"
                  aria-label="Grouped by this column"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'pt-1 pb-1.5 text-[11px] font-semibold text-stone-500',
        className
      )}
    >
      {children}
    </div>
  );
}

function MenuDivider() {
  return <div className="my-1 h-px bg-stone-100" />;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5rem_1fr] items-start gap-2 mb-1.5">
      <label className="text-xs text-stone-500 pt-1.5">{label}</label>
      <div>{children}</div>
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
